import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppState } from '../context/AppStateContext';
import ChildAvatar from './ChildAvatar';

interface Props {
  onSelectChild?: (childId: string | null) => void;
  selectedChildId?: string | null;
  filtersVisible?: boolean;
  onReload?: () => void;
  isReloading?: boolean;
  /** When true, prepend a "clear filter" avatar at the top of the quicklist. */
  showClearAvatar?: boolean;
  /** Supplied by sections that can show the full-area child picker — renders
   *  the compact/full toggle. Omitted elsewhere (no picker to switch to). */
  onExpandFull?: () => void;
}

// Switch to `true` to re-enable the popup dropdown variant for the selected state
// (the one that escapes the sidebar to the right and shows full names).
// When `false` (default), the sidebar shows the big selected avatar + caret, and
// clicking the caret expands the full vertical child list inline below it.
const USE_DROPDOWN_VARIANT = false;

const TRIGGER_SIZE = 82;
const QUICKLIST_SIZE = 56;

const ChildProfilesSidebar: React.FC<Props> = ({ onSelectChild, selectedChildId, showClearAvatar = false, onExpandFull }) => {
  const { childProfiles, setSelectedChildProfile } = useAppState();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLElement>(null);

  // Only active children are choosable here. Shared app state may still hold
  // the Admin screen's list — which deliberately includes inactive children so
  // they can be reactivated — for the moment between arriving here and this
  // section's own fetch landing, so filter at the point of display rather than
  // trusting whatever the last screen loaded.
  const list = useMemo(
    () => (childProfiles || []).filter((c) => c.is_active),
    [childProfiles]
  );

  // Looked up in the unfiltered set: a child deactivated elsewhere while open
  // should still show in the trigger rather than blanking it.
  const selectedChild = useMemo(
    () => (selectedChildId ? (childProfiles || []).find((c) => String(c.id) === selectedChildId) : null),
    [childProfiles, selectedChildId]
  );

  // Close the expanded list whenever the selection changes (tidy UX)
  useEffect(() => {
    setOpen(false);
  }, [selectedChildId]);

  // Dropdown outside-click + escape handling (only engages when the popup variant is enabled)
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSelect = (cId: number | null) => {
    if (onSelectChild) {
      onSelectChild(cId == null ? null : String(cId));
    } else {
      const child = cId == null ? null : ((childProfiles || []).find((c) => c.id === cId) || null);
      setSelectedChildProfile(child);
    }
    setOpen(false);
  };

  if (list.length === 0) {
    return <aside className="child-sidebar" aria-label="Child profiles sidebar" />;
  }

  // ─── Popup dropdown variant (preserved; enable via USE_DROPDOWN_VARIANT) ────
  if (USE_DROPDOWN_VARIANT && selectedChild) {
    return (
      <aside className="child-sidebar" aria-label="Child profiles sidebar">
        <section className="filter-group" aria-label="Filter by child">
          <div className="filter-group__label">Child</div>

          <div className="filter-group__dropdown-wrap" ref={wrapRef as React.Ref<HTMLDivElement>}>
            <button
              type="button"
              className={`filter-group__trigger ${open ? 'is-open' : ''}`}
              onClick={() => setOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={open}
            >
              <div className="filter-group__trigger-avatar">
                {selectedChild ? (
                  <ChildAvatar
                    child={selectedChild}
                    size={TRIGGER_SIZE}
                    imageUrl={selectedChild.profile_image_url}
                    showBadge={true}
                  />
                ) : (
                  <div className="filter-group__trigger-placeholder" aria-hidden="true">
                    <i className="fas fa-user" />
                  </div>
                )}
              </div>
              <span className="filter-group__trigger-hint">
                {!selectedChild && <span>All</span>}
                <i className="fas fa-chevron-down" aria-hidden="true" />
              </span>
            </button>

            {open && (
              <div className="filter-group__dropdown" role="listbox" aria-label="Choose a child">
                <button
                  type="button"
                  role="option"
                  aria-selected={!selectedChild}
                  className={`filter-group__option ${!selectedChild ? 'is-selected' : ''}`}
                  onClick={() => handleSelect(null)}
                >
                  <span className="filter-group__option-circle filter-group__option-circle--empty">
                    <i className="fas fa-asterisk" aria-hidden="true" />
                  </span>
                  <span className="filter-group__option-name filter-group__option-name--muted">
                    All children
                  </span>
                  {!selectedChild && (
                    <i className="fas fa-check filter-group__option-check" aria-hidden="true" />
                  )}
                </button>

                <div className="filter-group__option-sep" />

                {list.map((c) => {
                  const selected = String(c.id) === selectedChildId;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`filter-group__option ${selected ? 'is-selected' : ''}`}
                      onClick={() => handleSelect(c.id)}
                    >
                      <span
                        className="filter-group__option-circle"
                        style={{ background: c.colour_code || '#9aa89a' }}
                      >
                        {c.initials}
                      </span>
                      <span className="filter-group__option-name">
                        {c.first_name} {c.last_name}
                      </span>
                      {selected && (
                        <i className="fas fa-check filter-group__option-check" aria-hidden="true" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </aside>
    );
  }

  // ─── Default: big selected avatar + caret, expands inline to full list ─────
  return (
    <aside className="child-sidebar" aria-label="Child profiles sidebar" data-help="child-rail">
      <section className="filter-group" aria-label="Filter by child" ref={wrapRef}>
        <div className="filter-group__labelrow">
          <span className="filter-group__label">Children</span>
          {onExpandFull && (
            <button
              type="button"
              className="filter-group__expand"
              onClick={onExpandFull}
              title="Show all children full screen"
              aria-label="Show all children full screen"
            >
              <i className="fas fa-table-cells-large" aria-hidden="true" />
            </button>
          )}
        </div>

        {(() => {
          // Show the collapsed trigger when a specific child is selected,
          // OR (on the weekly schedule) when "All" is the active filter.
          const showTrigger = !!selectedChild || showClearAvatar;
          const listCollapsed = showTrigger && !open;
          return (
            <>
              {showTrigger && (
                <button
                  type="button"
                  className={`filter-group__trigger ${open ? 'is-open' : ''}`}
                  onClick={() => setOpen((v) => !v)}
                  aria-expanded={open}
                  aria-haspopup="true"
                  title={selectedChild ? `${selectedChild.first_name} ${selectedChild.last_name}` : 'All children'}
                >
                  <div className="filter-group__trigger-avatar">
                    {selectedChild ? (
                      <ChildAvatar
                        child={selectedChild}
                        size={TRIGGER_SIZE}
                        imageUrl={selectedChild.profile_image_url}
                        showBadge={true}
                      />
                    ) : (
                      <div className="filter-group__trigger-all" aria-hidden="true">
                        <span>All</span>
                      </div>
                    )}
                  </div>
                  <span className="filter-group__trigger-hint">
                    <i className="fas fa-chevron-down" aria-hidden="true" />
                  </span>
                </button>
              )}

              <div
                className={`filter-group__quicklist ${listCollapsed ? 'is-collapsed' : ''}`}
                aria-hidden={listCollapsed}
              >
          {showClearAvatar && (
            <div
              className={`filter-group__quicklist-item filter-group__quicklist-item--clear ${!selectedChild ? 'is-selected' : ''}`}
              role="button"
              tabIndex={listCollapsed ? -1 : 0}
              aria-pressed={!selectedChild}
              title="Show all (clear filter)"
              onClick={() => handleSelect(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSelect(null);
                  e.preventDefault();
                }
              }}
            >
              <div className="filter-group__clear-avatar" aria-hidden="true">
                <span className="filter-group__clear-avatar-text">All</span>
              </div>
            </div>
          )}
          {list.map((c) => {
            const isSelected = String(c.id) === selectedChildId;
            return (
              <div
                key={c.id}
                className={`filter-group__quicklist-item ${isSelected ? 'is-selected' : ''}`}
                role="button"
                tabIndex={listCollapsed ? -1 : 0}
                aria-pressed={isSelected}
                title={`${c.first_name} ${c.last_name}`}
                onClick={() => handleSelect(isSelected ? null : c.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleSelect(isSelected ? null : c.id);
                    e.preventDefault();
                  }
                }}
              >
                <ChildAvatar
                  child={c}
                  size={QUICKLIST_SIZE}
                  imageUrl={c.profile_image_thumbnail_url}
                  showBadge={true}
                />
              </div>
            );
          })}
              </div>
            </>
          );
        })()}
      </section>
    </aside>
  );
};

export default ChildProfilesSidebar;
